import type { FC } from 'react'
import { useOutletContext } from 'react-router'
import { Chip } from '@/components/commons/chip'
import { ErrorBox } from '@/components/commons/error-box'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { AlertCircleIcon, XCloseIcon } from '@/components/ui/icons'
import { ListHeader } from '@/components/ui/list-header'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { Skeleton } from '@/components/ui/skeleton'
import { useGetImagesMetadata } from '@/features/photoset/queries/use-get-images-metadata'
import {
	type Metadata,
	PhotosetImagesSearchParams,
	type PhotosetURLParams
} from '@/features/photoset/types'
import { formatMetadataValue } from '@/features/photoset/utils'
import { groupBy, objectKeys } from '@/helpers/utils'
import { useParsedSearchParams } from '@/hooks/use-parsed-search-params'
import { useTranslation } from '@/translations/i18n'

interface ShootListMetadataSidebarProps {
	hideSidebar: () => void
}

export const ShootListMetadataSidebar: FC<ShootListMetadataSidebarProps> = ({
	hideSidebar
}) => {
	const { t } = useTranslation()
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()

	const metadataQuery = useGetImagesMetadata(
		{
			urlParams: {
				photosetId: urlParams.id
			}
		},
		{ staleTime: Number.POSITIVE_INFINITY }
	)

	const { searchParams, setSearchParams } = useParsedSearchParams(
		PhotosetImagesSearchParams
	)

	const metadataDictionary = metadataQuery.data?.length
		? groupBy(metadataQuery.data, ({ type }) => type)
		: null

	const onFilterImages = (typeKey: Metadata['type'], typeValue: string) => {
		setSearchParams(
			searchParams.filter_by === typeKey &&
				searchParams.filter_value === typeValue
				? {
						filter_by: null,
						filter_value: null
					}
				: {
						filter_by: typeKey,
						filter_value: typeValue
					}
		)
	}

	return (
		<div className='grid w-[400px] grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)] overflow-hidden'>
			<ListHeader
				endSlot={
					<ButtonLegacy
						onClick={hideSidebar}
						shape='square'
						size='sm'
						type='button'
						variant='tertiary'
					>
						<XCloseIcon />
					</ButtonLegacy>
				}
				size='sm'
				title={t('photoset:initial_qa.sidebar_title')}
			/>

			{metadataQuery.isError ? (
				<ErrorBox
					icon={<AlertCircleIcon />}
					title={t('errors:default')}
				/>
			) : (
				<ScrollArea scrollbar={<Scrollbar />}>
					<div className='grid grid-cols-[minmax(0,1fr)] px-3 py-6'>
						{metadataQuery.isPending
							? Array.from(
									{ length: 10 },
									(_, i) => `skeleton-${i}`
								).map((key) => (
									<div
										className='flex gap-4 border-neutral-200 border-b px-3 py-2 text-xs last:border-none'
										key={key}
									>
										<Skeleton height={40} width={110} />
										<div className='flex gap-2'>
											<Skeleton height={24} />
											<Skeleton height={24} />
										</div>
									</div>
								))
							: metadataDictionary &&
								objectKeys(metadataDictionary).map(
									(typeKey) => (
										<div
											className='flex gap-4 border-neutral-200 border-b px-3 py-2 text-xs last:border-none'
											key={typeKey}
										>
											<div className='min-w-[110px]'>
												{t(
													`photoset:metadata.${typeKey}`
												)}
											</div>

											<div className='flex flex-wrap items-stretch gap-2 overflow-hidden'>
												{metadataDictionary[
													typeKey
												].map((item) => (
													<Chip
														className='text-left'
														endSlot={
															<div
																className={`rounded-sm px-1 ${
																	searchParams.filter_by ===
																		item.type &&
																	searchParams.filter_value ===
																		item.value
																		? 'bg-success-100'
																		: 'bg-neutral-100'
																}`}
															>
																{item.count}
															</div>
														}
														isActive={
															searchParams.filter_by ===
																item.type &&
															searchParams.filter_value ===
																item.value
														}
														key={item.value}
														label={formatMetadataValue(
															item.type,
															item.value
														)}
														onClick={() =>
															onFilterImages(
																typeKey,
																item.value
															)
														}
														variant='success'
													/>
												))}
											</div>
										</div>
									)
								)}
					</div>
				</ScrollArea>
			)}
		</div>
	)
}
